<template>
  <div>
    <organism-executive-dashboard
      :ticketStatusCount="ticketStatusCount"
      :isLoadingTicketPriority="isLoadingTicketPriority"
      :ticketPriorityCount="ticketPriorityCount"
      :ticketPriorityList="ticketPriorityList"
      :isLoadingTicketCriticalEntry="isLoadingTicketCriticalEntry"
      :ticketCriticalEntryList="ticketCriticalEntryList"
      :isLoadingTicketEscalation="isLoadingTicketEscalation"
      :ticketEscalationList="ticketEscalationList"
      :isLoadingTicketRecentChange="isLoadingTicketRecentChange"
      :ticketRecentChangeList="ticketRecentChangeList"
      :isLoadingTicketEntryStat="isLoadingTicketEntryStat"
      :ticketEntryStatTimeList="ticketEntryStatTimeList"
      :ticketEntryStat="ticketEntryStat"
      @changeTicketEntryStatTime="changeTicketEntryStatTime"
    ></organism-executive-dashboard>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import OrganismExecutiveDashboard from "@/components/organisms/portal/internal/dashboard/OrganismExecutiveDashboard.vue"
import * as dashboardApi from "@/api/api-module1-dashboard"
import axios from "axios"
import { TicketType } from "@/constants"

export default {
  name: "ExecutiveDashboard",
  components: { OrganismExecutiveDashboard },
  mixins: [mixinsDateTime, mixinsSiteHelper],
  data() {
    return {
      // TICKET STATUS COUNT
      ticketStatusCount: {
        open: "0",
        inProgress: "0",
        isLoading: true,
      },

      // TICKET PRIORITY COUNT
      isLoadingTicketPriority: true,
      ticketPriorityCount: 0,
      ticketPriorityList: [
        {
          name: "Critical",
          value: 0,
          color: "#584CDB",
        },
        {
          name: "High",
          value: 0,
          color: "#9087F3",
        },
        {
          name: "Medium",
          value: 0,
          color: "#B6B0F7",
        },
        {
          name: "Low",
          value: 0,
          color: "#E3E1FC",
        },
      ],

      // TICKET CRITICAL ENTRY
      isLoadingTicketCriticalEntry: true,
      ticketCriticalEntryList: [],

      // TICKET ESCALATION
      isLoadingTicketEscalation: true,
      ticketEscalationList: [],

      // TICKET RECENT CHANGE
      isLoadingTicketRecentChange: true,
      ticketRecentChangeList: [],

      // TICKET NEW ENTRY STATISTICS
      isLoadingTicketEntryStat: true,
      ticketEntryStatTimeList: ["1M", "3M", "6M", "1Y"],
      ticketEntryStat: {
        timestampList: [],
        dateList: [],
      },

      intervalId: 0,
    }
  },
  computed: {
    ...mapGetters("appAutoRefresh", ["getWPMExecutiveDashboardAutoRefresh"]),
    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),

    isEnableAutoRefresh() {
      return this.getWPMExecutiveDashboardAutoRefresh
    },
  },
  async mounted() {
    this.setIsShowAutoRefresh(true)
    await this.fetchData()
  },
  beforeDestroy() {
    this.setIsShowAutoRefresh(false)
    window.clearInterval(this.intervalId)
  },
  watch: {
    async siteSetting() {
      await this.fetchData()
    },

    isEnableAutoRefresh: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isEnableAutoRefresh) {
          this.intervalId = window.setInterval(() => {
            this.setIsLoadingAllTrue()
            this.fetchData()
          }, 60000)
        } else {
          window.clearInterval(this.intervalId)
        }
      },
    },
  },
  methods: {
    ...mapActions("appAutoRefresh", ["setIsShowAutoRefresh"]),

    async fetchData() {
      this.setIsLoadingAllTrue()
      await axios.all([
        this.getTicketStatusCount(),
        this.getTicketPriority(),
        this.getTicketCriticalEntry(),
        this.getTicketEscalation(),
        this.getTicketRecentChange(),
        // this.getTicketEntryStat("1M"),
      ])
    },

    async getTicketStatusCount() {
      const filterData = this.filterDecoration()
      await dashboardApi.getExecutiveTicketStat(filterData).then((response) => {
        const openedStat = response.data.stats.find(
          (item) => item.name === "OPENED"
        )
        const inProgressStat = response.data.stats.find(
          (item) => item.name === "IN_PROGRESS"
        )

        this.ticketStatusCount = {
          open: openedStat ? openedStat.count : 0,
          inProgress: inProgressStat ? inProgressStat.count : 0,
          isLoading: false,
        }
      })
    },

    async getTicketPriority() {
      const filterData = this.filterDecoration()
      await dashboardApi
        .getExecutiveTicketStat({
          groupBy: "priority",
          Status: ["OPENED", "IN_PROGRESS"],
          ...filterData,
        })
        .then((response) => {
          this.ticketPriorityCount = response.data.total

          response.data.stats.forEach(({ name, count }) => {
            if (name === "CRITICAL") {
              this.ticketPriorityList[0].value = count || 0
            } else if (name === "HIGH") {
              this.ticketPriorityList[1].value = count || 0
            } else if (name === "MEDIUM") {
              this.ticketPriorityList[2].value = count || 0
            } else if (name === "LOW") {
              this.ticketPriorityList[3].value = count || 0
            }
          })
        })
        .finally(() => {
          this.isLoadingTicketPriority = false
        })
    },

    async getTicketCriticalEntry() {
      this.ticketCriticalEntryList = []
      const filterData = this.filterDecoration()
      await dashboardApi
        .getExecutiveTicketCriticalEntry(filterData)
        .then((response) => {
          this.ticketCriticalEntryList = response.data.map((entry) => ({
            id: entry.ticketId,
            idLabel: `${this.getTicketType(entry.ticketType)} ${
              entry.ticketId
            }`,
            companyName: entry.contractCustomerName,
            idcId: entry.contractId
              ? entry.contractId.toString().padStart(8, "0")
              : null,
            subject: entry.ticketSubject,
            detail: entry.ticketDetail,
            date: this.setDateTimeFormat(entry.updatedAt, "dd/MM/yyyy"),
            time: this.setDateTimeFormat(entry.updatedAt, "HH:mm:ss"),
            siteName: entry?.siteName,
          }))
        })
        .finally(() => {
          this.isLoadingTicketCriticalEntry = false
        })
    },

    async getTicketEscalation() {
      this.ticketEscalationList = []
      const filterData = this.filterDecoration()
      await dashboardApi
        .getExecutiveTicketEscalation(filterData)
        .then((response) => {
          this.ticketEscalationList = response.data.map((entry) => ({
            id: entry.ticketId,
            idLabel: `${this.getTicketType(entry.ticketType)} ${
              entry.ticketId
            }`,
            companyName: entry.customerName,
            idcId: entry.idcId ? entry.idcId.toString().padStart(8, "0") : null,
            subject: entry.subject,
            lastReply: entry.lastReply,
            level: entry.level,
            escalateBy: entry.escalateBy,
            current: entry.currentResponsiblePerson,
            status: entry.status,
            date: this.setDateTimeFormat(entry.escalateTime, "dd/MM/yyyy"),
            time: this.setDateTimeFormat(entry.escalateTime, "HH:mm:ss"),
            isUserLevel: entry.isUserLevel,
            siteName: entry?.siteName,
          }))
        })
        .finally(() => {
          this.isLoadingTicketEscalation = false
        })
    },

    async getTicketRecentChange() {
      this.ticketRecentChangeList = []
      const filterData = this.filterDecoration()
      await dashboardApi
        .getExecutiveTicketRecentEntry(filterData)
        .then((response) => {
          this.ticketRecentChangeList = response.data.map((entry) => ({
            id: entry.ticketId,
            idLabel: `${this.getTicketType(entry.ticketType)} ${
              entry.ticketId
            }`,
            companyName: entry.customerName,
            idcId: entry.contractId
              ? entry.contractId.toString().padStart(8, "0")
              : null,
            priority: entry.priority,
            subject: entry.subject,
            staff: entry.createdBy,
            activity: entry.message,
            status: entry.status,
            date: this.setDateTimeFormat(entry.updatedAt, "dd/MM/yyyy"),
            time: this.setDateTimeFormat(entry.updatedAt, "HH:mm:ss"),
            siteName: entry?.siteName,
          }))
        })
        .finally(() => {
          this.isLoadingTicketRecentChange = false
        })
    },

    getTicketType(ticketType) {
      switch (ticketType) {
        case TicketType.NORMAL:
          return "Ticket"
        case TicketType.INCIDENT:
          return "Incident"
        case TicketType.JOB:
          return "Job"
        default:
          return ticketType
      }
    },

    // async getTicketEntryStat(time) {
    //   this.ticketEntryStat.timestampList = []
    //   this.ticketEntryStat.dataList = [
    //     {
    //       name: "Ticket Tracking",
    //       color: "#7367F0",
    //       itemColor: "#B6AFF4",
    //       data: [],
    //     },
    //   ]
    //   const filterData = this.filterDecoration()
    //   await dashboardApi
    //     .getExecutiveNewEntryStat({ timeRange: time, ...filterData })
    //     .then((response) => {
    //       response.data.ticket_tracking.forEach(({ timestamp, value }) => {
    //         this.ticketEntryStat.timestampList.push(
    //           this.setDateTimeFormat(timestamp, "dd/MM/yyyy")
    //         )
    //         this.ticketEntryStat.dataList[0].data.push(value)
    //       })
    //     })
    //     .finally(() => {
    //       this.isLoadingTicketEntryStat = false
    //     })
    // },

    changeTicketEntryStatTime(time) {
      // this.getTicketEntryStat(time)
    },

    filterDecoration() {
      return { SiteIds: this.siteOptions.map((site) => site.value) }
    },

    setIsLoadingAllTrue() {
      this.ticketStatusCount.isLoading = true

      this.isLoadingTicketPriority = true
      this.isLoadingTicketCriticalEntry = true
      this.isLoadingTicketEscalation = true
      this.isLoadingTicketRecentChange = true
      this.isLoadingTicketEntryStat = false
    },
  },
}
</script>

<style></style>
